import React, {
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { List } from '@bingads-webui-react/fabric-control';
import { TilesGridLoadingCell, TilesGridCellImage, TilesGridCellSelectionDecorator } from '@bingads-webui-campaign-react/asset-library-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';

import { getImageStyle, getListStyle, IMAGE_WIDTH, IMAGE_HEIGHT } from './styles/index';

const FIRST_LOADING_CELL = {};
const LOADING_CELL = {};

const CELLS_PER_ROW = 3;

export const FluentImagePicker = ({
  images,
  loadMoreImages,
  onSelectImage,
  loadingPaddingCount,
  pageSize,
  hasMoreImages,
  imageCellWidth,
  imageCellHeight,
  isMultiSelect,
}) => {
  const i18n = useContext(I18nContext);
  const getStyles = useMemo(
    () => getImageStyle(imageCellWidth, imageCellHeight),
    [imageCellHeight, imageCellWidth]
  );
  const imageClasses = useDefaultStyles(getStyles);
  const listClasses = useDefaultStyles(getListStyle);

  const getPageHeight = useCallback(() => Math.ceil(pageSize / CELLS_PER_ROW) * IMAGE_HEIGHT, [
    pageSize,
  ]);

  const keyDownHandler = useCallback((index, event) => {
    const imageContainers = document.querySelectorAll('.item-container');
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        if (index > 0) {
          imageContainers[index - 1].focus();
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        if (index < imageContainers.length - 1) {
          imageContainers[index + 1].focus();
        }
        break;
      case 'Enter':
        onSelectImage(index);
        break;
      case 'Tab':
        imageContainers[index].blur();
        break;
      default:
        break;
    }

    event.stopPropagation();
    event.preventDefault();
  }, [onSelectImage]);

  const onRenderCell = useCallback((item, index) => {
    let pickerItem;

    if (item === FIRST_LOADING_CELL) {
      pickerItem = (
        <TilesGridLoadingCell
          containerClass={classnames(imageClasses.imageContainer, 'first-loading-cell', 'loading-cell', 'cell')}
          onLoading={loadMoreImages}
        >
          <AssetLibrarySpinner />
        </TilesGridLoadingCell>
      );
    } else if (item === LOADING_CELL) {
      pickerItem = (
        <TilesGridLoadingCell
          containerClass={classnames(imageClasses.imageContainer, 'loading-cell', 'cell')}
        >
          <AssetLibrarySpinner />
        </TilesGridLoadingCell>
      );
    } else {
      const containerClass = item.isSelected ?
        classnames(isMultiSelect ? imageClasses.selectedImageContainerWithMultiSelect : imageClasses.selectedImageContainer, 'selected-image', 'image-container', 'cell') :
        classnames(isMultiSelect ? imageClasses.imageContainerWithMultiSelect : imageClasses.imageContainer, 'unselected-image', 'image-container', 'cell');
      pickerItem = (
        <React.Fragment>
          {
            isMultiSelect &&
              <TilesGridCellSelectionDecorator
                item={item}
                selected={item.isSelected || false}
                onClick={() => onSelectImage(index)}
                classes={{
                  cellSelectionDecorator: imageClasses.cellSelectionDecorator,
                  selectedState: imageClasses.selectedState,
                  cellSelectionDecoratorWrapper: imageClasses.cellSelectionDecoratorWrapper,
                  selectedStateWrapper: imageClasses.selectedStateWrapper,
                }}
                ariaLabel={i18n.getString(_TL_('Image'))}
              >
                <TilesGridCellImage
                  src={item.thumbnailSrc}
                  alt=""
                  i18n={i18n}
                  classes={{
                    containerClass,
                    imageClass: imageClasses.image,
                  }}
                />
              </TilesGridCellSelectionDecorator>
          }
          {
            !isMultiSelect &&
              <TilesGridCellImage
                src={item.thumbnailSrc}
                alt=""
                i18n={i18n}
                onClick={() => onSelectImage(index)}
                classes={{
                  containerClass,
                  imageClass: imageClasses.image,
                }}
              />
          }
          { !isMultiSelect && item.isSelected && <div className={classnames(imageClasses.selectedIcon, 'selectedIcon')} /> }
        </React.Fragment>
      );
    }

    return (
      <div
        className={classnames(imageClasses.itemContainer, 'item-container')}
        role="option"
        aria-selected={index === 0}
        id={`listbox-${index + 1}`}
        tabIndex={index === 0 ? 0 : -1}
        onKeyDown={(event) => { keyDownHandler(index, event); }}
      >
        {pickerItem}
      </div>
    );
  }, [imageClasses.itemContainer, imageClasses.imageContainer, imageClasses.selectedImageContainerWithMultiSelect, imageClasses.selectedImageContainer, imageClasses.imageContainerWithMultiSelect, imageClasses.cellSelectionDecorator, imageClasses.selectedState, imageClasses.image, imageClasses.selectedIcon, loadMoreImages, isMultiSelect, i18n, onSelectImage, keyDownHandler]);


  const allImages = useMemo(() => {
    if (images.length > 0 && hasMoreImages) {
      return [
        ...images,
        FIRST_LOADING_CELL,
        ...Array(loadingPaddingCount - 1).fill(LOADING_CELL),
      ];
    }
    return images;
  }, [images, loadingPaddingCount, hasMoreImages]);

  return (
    <div className={classnames(listClasses.listContainer, 'fluent-image-picker')}>
      <List
        items={allImages}
        onRenderCell={onRenderCell}
        getItemCountForPage={() => pageSize}
        getPageHeight={getPageHeight}
        role="listbox"
      />
    </div>
  );
};

FluentImagePicker.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
    isSelected: PropTypes.bool,
  })),
  loadMoreImages: PropTypes.func.isRequired,
  onSelectImage: PropTypes.func.isRequired,
  hasMoreImages: PropTypes.bool,
  loadingPaddingCount: PropTypes.number,
  pageSize: PropTypes.number,
  imageCellWidth: PropTypes.number,
  imageCellHeight: PropTypes.number,
  isMultiSelect: PropTypes.bool,
};

FluentImagePicker.defaultProps = {
  images: [],
  loadingPaddingCount: 20,
  pageSize: 20,
  hasMoreImages: false,
  imageCellWidth: IMAGE_WIDTH,
  imageCellHeight: IMAGE_HEIGHT,
  isMultiSelect: false,
};
